import { format } from 'date-fns';

export const DATE_FORMAT = 'MMM d, yyyy';
export const DATE_TIME_FORMAT = 'MMM d, yyyy h:mm a';

type DateInput = Date | string | number | null | undefined;

const toDate = (input: DateInput): Date | null => {
  if (input == null || input === '') return null;
  const date = input instanceof Date ? input : new Date(input);
  return isNaN(date.getTime()) ? null : date;
};

export const formatDate = (input: DateInput): string => {
  const date = toDate(input);
  return date ? format(date, DATE_FORMAT) : '';
};

export const formatDateTime = (input: DateInput): string => {
  const date = toDate(input);
  return date ? format(date, DATE_TIME_FORMAT) : '';
};

/**
 * Parses a `YYYY-MM-DD` calendar date into local midnight.
 *
 * Calendar dates — a birth date being the obvious one — name a day, not an
 * instant, and travel as `YYYY-MM-DD` (the GraphQL `Date` scalar) so no UTC
 * offset can ride along. Routing one through `new Date('1995-06-15')` instead
 * parses it as UTC midnight, which renders as the 14th anywhere west of
 * Greenwich; anchoring to local midnight means date-fns' local getters read
 * back the day that was actually stored, in every timezone.
 *
 * A full ISO timestamp is tolerated so frozen transcript snapshots written
 * before the scalar change still render — their UTC day is used.
 */
export const parseCalendarDate = (
  input: string | null | undefined
): Date | null => {
  if (!input) return null;
  const [year, month, day] = input.slice(0, 10).split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

/**
 * The inverse: turns a date picked in the browser into the `YYYY-MM-DD` the
 * API expects. Reads the local getters, so it yields the day the user clicked
 * rather than whatever day that midnight lands on in UTC.
 */
export const toCalendarDate = (date: Date): string =>
  format(date, 'yyyy-MM-dd');

export const formatCalendarDate = (
  input: string | null | undefined
): string => {
  const date = parseCalendarDate(input);
  return date ? format(date, DATE_FORMAT) : '';
};
